import {
  ArrowLeft,
  FileText,
  Flag,
  MessageSquare,
  AlertTriangle,
  UserCheck,
  RotateCcw,
  Archive,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "../../components/ui/avatar";
import { Badge } from "../../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "../../components/ui/alert-dialog";

import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { adminApi } from "../utils/admin-api-axios";

export const UserDetail = ({
  setSelectedUser,
  selectedUser,
  handleChatClick,
  userListings,
  listingLoading,
  userConversations,
  conversationLoading,
  userReports,
  reportsLoading,
  refreshUserData, // New prop to refresh data after actions
}) => {
  const navigate = useNavigate();
  const [actionLoading, setActionLoading] = useState({});
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const totalChats = userConversations.length;

  const flaggedChats = userConversations.filter(
    (chat) => chat.isReported === true || chat.flagged === true
  ).length;

  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Get status badge variant
  const getStatusBadgeVariant = (status) => {
    switch (status?.toLowerCase()) {
      case "pending":
        return "secondary";
      case "resolved":
        return "default";
      case "dismissed":
        return "outline";
      case "active":
        return "default";
      case "inactive":
        return "secondary";
      case "removed":
        return "destructive";
      case "archived":
        return "outline";
      case "flagged":
        return "destructive";
      default:
        return "secondary";
    }
  };

  // Get report type display text
  const getReportTypeDisplay = (type) => {
    switch (type?.toLowerCase()) {
      case "user":
        return "User";
      case "listing":
        return "Listing";
      case "chat":
        return "Chat";
      default:
        return type || "Unknown";
    }
  };

  const handleViewReport = (reportId) => {
    navigate("/admin/reports", {
      state: {
        reportId: reportId,
        autoOpenReport: true,
      },
    });
  };

  // Success dialog helper
  const showSuccess = (message) => {
    setSuccessMessage(message);
    setShowSuccessDialog(true);
  };

  // Handle unban user
  const handleUnbanUser = async () => {
    try {
      setActionLoading({ ...actionLoading, unbanUser: true });
      await adminApi.unbanUser(selectedUser._id);
      showSuccess("User has been successfully unbanned!");
      // Refresh data
      if (refreshUserData) {
        refreshUserData();
      }
    } catch (error) {
      console.error("Error unbanning user:", error);
      alert("Failed to unban user: " + error.message);
    } finally {
      setActionLoading({ ...actionLoading, unbanUser: false });
    }
  };

  // Handle reactivate listing
  const handleReactivateListing = async (listingId, listingTitle) => {
    try {
      setActionLoading({ ...actionLoading, [`listing-${listingId}`]: true });
      await adminApi.reactivateListing(listingId);
      showSuccess(
        `Listing "${listingTitle}" has been successfully reactivated!`
      );
      // Refresh data
      if (refreshUserData) {
        refreshUserData();
      }
    } catch (error) {
      console.error("Error reactivating listing:", error);
      alert("Failed to reactivate listing: " + error.message);
    } finally {
      setActionLoading({ ...actionLoading, [`listing-${listingId}`]: false });
    }
  };

  // Handle restore conversation
  const handleRestoreConversation = async (conversationId, participants) => {
    try {
      setActionLoading({
        ...actionLoading,
        [`conversation-${conversationId}`]: true,
      });
      await adminApi.restoreConversation(conversationId);
      const participantNames = participants
        .map((p) => `${p.firstName} ${p.lastName}`)
        .join(" & ");
      showSuccess(
        `Conversation between ${participantNames} has been successfully restored!`
      );
      // Refresh data
      if (refreshUserData) {
        refreshUserData();
      }
    } catch (error) {
      console.error("Error restoring conversation:", error);
      alert("Failed to restore conversation: " + error.message);
    } finally {
      setActionLoading({
        ...actionLoading,
        [`conversation-${conversationId}`]: false,
      });
    }
  };

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <Button
        variant="outline"
        onClick={() => setSelectedUser(null)}
        className="mb-4"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Users
      </Button>

      {/* User Details */}
      <Card>
        <CardHeader>
          <CardTitle>User Details</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="flex items-center space-x-4">
                <Avatar className="w-16 h-16">
                  <AvatarImage src={selectedUser.image} />
                  <AvatarFallback className="text-lg">
                    {selectedUser.firstName
                      .split(" ")
                      .map((n) => n[0])
                      .join("")}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-xl font-semibold">
                      {selectedUser.firstName + " " + selectedUser.lastName}
                    </h3>
                    {/* Unban Button - only show if user is banned */}
                    {selectedUser.isBanned && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-green-600 border-green-600 hover:bg-green-50"
                            disabled={actionLoading.unbanUser}
                          >
                            <UserCheck className="w-4 h-4 mr-1" />
                            {actionLoading.unbanUser
                              ? "Unbanning..."
                              : "Unban User"}
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Unban User</AlertDialogTitle>
                            <AlertDialogDescription>
                              Are you sure you want to unban{" "}
                              {selectedUser.firstName} {selectedUser.lastName}?
                              This will restore their account access and
                              reactivate their listings.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={handleUnbanUser}
                              className="bg-green-600 hover:bg-green-700"
                            >
                              Unban User
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </div>
                  <Badge
                    variant={
                      selectedUser.isBanned
                        ? "destructive"
                        : selectedUser.status === "Active"
                        ? "default"
                        : "destructive"
                    }
                  >
                    {selectedUser.isBanned ? "Banned" : selectedUser.status}
                  </Badge>
                  {selectedUser.isBanned && selectedUser.banReason && (
                    <p className="text-sm text-red-600 mt-1">
                      <strong>Ban Reason:</strong> {selectedUser.banReason}
                    </p>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <p>
                  <strong>Email:</strong> {selectedUser.email}
                </p>
                <p>
                  <strong>Join Date:</strong>{" "}
                  {selectedUser.createdAt
                    ? new Date(selectedUser.createdAt).toLocaleDateString(
                        "en-US",
                        {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        }
                      )
                    : "N/A"}
                </p>
                <p>
                  <strong>Last Active:</strong>{" "}
                  {selectedUser.lastActiveAt
                    ? new Date(selectedUser.lastActiveAt).toLocaleString(
                        "en-US",
                        {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        }
                      )
                    : "N/A"}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Card>
                <CardContent className="!p-4 text-center">
                  <div className="text-2xl font-bold text-blue-600">
                    {userListings.length || "0"}
                  </div>
                  <div className="text-sm text-gray-600">Total Listings</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="!p-4 text-center">
                  <div className="text-2xl font-bold text-green-600">
                    {userListings.filter(
                      (listing) => listing.status === "active"
                    ).length || "0"}
                  </div>
                  <div className="text-sm text-gray-600">Active Listings</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="!p-4 text-center">
                  <div className="text-2xl font-bold text-purple-600">
                    {totalChats || "0"}
                  </div>
                  <div className="text-sm text-gray-600">Total Chats</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="!p-4 text-center">
                  <div className="text-2xl font-bold text-red-600">
                    {flaggedChats || "0"}
                  </div>
                  <div className="text-sm text-gray-600">Flagged Chats</div>
                </CardContent>
              </Card>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* User Listings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <FileText className="w-5 h-5 mr-2" />
            User Listings
          </CardTitle>
        </CardHeader>
        <CardContent>
          {listingLoading ? (
            <p>Loading listings...</p>
          ) : userListings.length === 0 ? (
            <p>No listing found for this user</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {userListings.map((listing) => (
                  <TableRow key={listing.id}>
                    <TableCell className="font-medium">
                      {listing.title}
                    </TableCell>
                    <TableCell>
                      {listing.description.length > 50
                        ? listing.description.slice(0, 50) + "..."
                        : listing.description}
                    </TableCell>
                    <TableCell>
                      {listing.location?.country?.name},{" "}
                      {listing.location?.province?.name}
                    </TableCell>
                    <TableCell>
                      <Badge variant={getStatusBadgeVariant(listing.status)}>
                        {listing.status.charAt(0).toUpperCase() +
                          listing.status.slice(1)}
                      </Badge>
                    </TableCell>
                    <TableCell>{formatDate(listing.createdAt)}</TableCell>
                    <TableCell>
                      {listing.status !== "active" ? (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-green-600 border-green-600 hover:bg-green-50"
                              disabled={actionLoading[`listing-${listing._id}`]}
                            >
                              <RotateCcw className="w-4 h-4 mr-1" />
                              {actionLoading[`listing-${listing._id}`]
                                ? "Reactivating..."
                                : "Reactivate"}
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>
                                Reactivate Listing
                              </AlertDialogTitle>
                              <AlertDialogDescription>
                                Are you sure you want to reactivate the listing
                                "{listing.title}"? This will make it visible to
                                users again.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() =>
                                  handleReactivateListing(
                                    listing._id,
                                    listing.title
                                  )
                                }
                                className="bg-green-600 hover:bg-green-700"
                              >
                                Reactivate
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      ) : (
                        <span className="text-sm text-gray-500">
                          No action needed
                        </span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* User Chats */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <MessageSquare className="w-5 h-5 mr-2" />
            User Chats
          </CardTitle>
        </CardHeader>
        <CardContent>
          {conversationLoading ? (
            <p>Loading conversations...</p>
          ) : userConversations.length === 0 ? (
            <p>No conversations found for this user</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Chat ID</TableHead>
                  <TableHead>Chat Participants</TableHead>
                  <TableHead>Last Message</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Flagged</TableHead>
                  <TableHead>Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {userConversations.map((chat) => (
                  <TableRow key={chat._id}>
                    <TableCell>{chat._id}</TableCell>
                    <TableCell className="font-medium">
                      {chat.participants
                        .map((p) => p.email.split("@")[0])
                        .join(" <=> ")}
                    </TableCell>
                    <TableCell>
                      {chat.lastMessage?.text
                        ? chat.lastMessage.text.length > 50
                          ? chat.lastMessage.text.slice(0, 50) + "..."
                          : chat.lastMessage.text
                        : "No messages yet"}
                    </TableCell>
                    <TableCell>
                      <Badge variant={getStatusBadgeVariant(chat.status)}>
                        {chat.status.charAt(0).toUpperCase() +
                          chat.status.slice(1)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {chat.isReported ? (
                        <Badge
                          variant="destructive"
                          className="flex items-center w-fit"
                        >
                          <Flag className="w-3 h-3 mr-1" />
                          Yes
                        </Badge>
                      ) : (
                        <Badge variant="secondary">No</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleChatClick(chat._id)}
                        >
                          View Chat
                        </Button>
                        {chat.status === "archived" && (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-green-600 border-green-600 hover:bg-green-50"
                                disabled={
                                  actionLoading[`conversation-${chat._id}`]
                                }
                              >
                                <Archive className="w-4 h-4 mr-1" />
                                {actionLoading[`conversation-${chat._id}`]
                                  ? "Restoring..."
                                  : "Restore"}
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>
                                  Restore Conversation
                                </AlertDialogTitle>
                                <AlertDialogDescription>
                                  Are you sure you want to restore this
                                  conversation? This will make it active again
                                  for the participants.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() =>
                                    handleRestoreConversation(
                                      chat._id,
                                      chat.participants
                                    )
                                  }
                                  className="bg-green-600 hover:bg-green-700"
                                >
                                  Restore
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* User Reports */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <AlertTriangle className="w-5 h-5 mr-2" />
            User Reports ({userReports?.length || 0})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {reportsLoading ? (
            <p>Loading reports...</p>
          ) : !userReports || userReports.length === 0 ? (
            <p>No reports submitted by this user</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Report ID</TableHead>
                  <TableHead>Reported User</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {userReports.map((report) => (
                  <TableRow key={report._id}>
                    <TableCell className="font-mono text-sm">
                      {report._id.slice(-8)}
                    </TableCell>
                    <TableCell>
                      {report.reportedUser
                        ? `${report.reportedUser.firstName} ${report.reportedUser.lastName}`
                        : "N/A"}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {getReportTypeDisplay(report.type)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {report.reason.length > 40
                        ? report.reason.slice(0, 40) + "..."
                        : report.reason}
                    </TableCell>
                    <TableCell>{formatDate(report.createdAt)}</TableCell>
                    <TableCell>
                      <Badge variant={getStatusBadgeVariant(report.status)}>
                        {report.status.charAt(0).toUpperCase() +
                          report.status.slice(1)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleViewReport(report._id)}
                      >
                        View Details
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Success Dialog */}
      <AlertDialog open={showSuccessDialog} onOpenChange={setShowSuccessDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Action Completed Successfully</AlertDialogTitle>
            <AlertDialogDescription>{successMessage}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setShowSuccessDialog(false)}>
              OK
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
